import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Circle } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';
import './App.css';
import { API_BASE_URL } from './apiConfig.js';

// Fallback sample data
const fallbackDataPoints = [
];

const getCircleColor = (value, dataType) => {
  if (dataType === 'voc') {
    if (value >= 600) return '#FF0000';
    if (value >= 200) return '#FFA500';
    return '#0000FF';
  }
  // CO2
  if (value >= 1500) return '#FF0000';
  if (value >= 800) return '#FFA500';
  return '#0000FF';
};

// Format date to YYYY-MM-DD HH:mm:ss in HKT
const formatHKTDate = (date) => {
  if (!date) return null;
  const options = {
    timeZone: 'Asia/Hong_Kong',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  };
  const formatter = new Intl.DateTimeFormat('en-US', options);
  const parts = formatter.formatToParts(date);
  const map = new Map(parts.map(p => [p.type, p.value]));
  return `${map.get('year')}-${map.get('month')}-${map.get('day')} ${map.get('hour')}:${map.get('minute')}:${map.get('second')}`;
};

// Convert datetime-local value (YYYY-MM-DDThh:mm) to YYYY-MM-DD HH:mm:ss
const formatDatetimeLocalToHKT = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (isNaN(date.getTime())) return null;
  return formatHKTDate(date);
};

export function Monitor() {
  const [selectedDataType, setSelectedDataType] = useState('co2');
  const [timeRange, setTimeRange] = useState('24h');
  const [endTime, setEndTime] = useState(formatHKTDate(new Date())); // Picker value
  const [useCurrentTime, setUseCurrentTime] = useState(true); // Current button state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dataPoints, setDataPoints] = useState([]);

  const fetchData = async () => {
    if (loading) return; // Prevent concurrent fetches
    setLoading(true);
    setError(null);

    try {
      // Fetch locations
      const locationsResponse = await axios.get(`${API_BASE_URL}/location/get-all`);
      console.log('Locations Response:', locationsResponse.data); // Debug

      // Validate locations response
      const locationsData = locationsResponse.data;
      if (!locationsData || !Array.isArray(locationsData.result)) {
        throw new Error('Invalid locations data format');
      }

      const locations = locationsData.result;
      if (!locations.length) {
        throw new Error('No location data available');
      }

      // Fetch sensor data with time range
      let startTime = null;
      const now = new Date();
      const effectiveEndTime = useCurrentTime ? formatHKTDate(now) : (endTime || formatHKTDate(now)); // Current or picker time

      switch (timeRange) {
        case '24h':
          startTime = formatHKTDate(new Date(new Date(effectiveEndTime).getTime() - 24 * 60 * 60 * 1000));
          break;
        case '7days':
          startTime = formatHKTDate(new Date(new Date(effectiveEndTime).getTime() - 7 * 24 * 60 * 60 * 1000));
          break;
        case 'all':
          startTime = null; // No start time for all data
          break;
        default:
          startTime = null;
      }

      const params = {
        startTime: startTime || undefined,
        endTime: timeRange === 'all' ? undefined : effectiveEndTime,
        useCurrentTime, // Debug
      };
      console.log('Sensor Request Params:', params); // Debug

      const sensorResponse = await axios.get(`${API_BASE_URL}/data/latest-location-data`, { params });
      console.log('Sensor Response:', sensorResponse.data); // Debug

      // Validate sensor response
      const sensorData = sensorResponse.data;
      if (!sensorData || sensorData.success === false || !Array.isArray(sensorData.result)) {
        console.warn('Sensor data invalid or empty, using fallback');
        setDataPoints(fallbackDataPoints);
        return;
      }

      // Create a map of locations by ID
      const locationMap = new Map(locations.map(loc => [loc.id, { lat: loc.lat, lng: loc.lon }]));

      // Combine data by matching locationId to location id
      const combinedData = sensorData.result
        .filter(sensor => locationMap.has(sensor.locationId))
        .map(sensor => ({
          lat: locationMap.get(sensor.locationId).lat,
          lng: locationMap.get(sensor.locationId).lng,
          co2: sensor.co2 ?? 0,
          voc: sensor.voc ?? 0,
          timestamp: sensor.timestamp || '',
        }));

      console.log('Combined Data:', combinedData); // Debug
      setDataPoints(combinedData.length ? combinedData : fallbackDataPoints);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch data';
      setError(errorMessage);
      console.error('API Error:', err);
      setDataPoints(fallbackDataPoints); // Use fallback data
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(); // Initial fetch
    const intervalId = setInterval(fetchData, 60 * 1000); // Fetch every 60 seconds
    return () => clearInterval(intervalId); // Cleanup
  }, [timeRange, endTime, useCurrentTime]);

  // Convert HKT (YYYY-MM-DD HH:mm:ss) to datetime-local value (YYYY-MM-DDThh:mm)
  const getDatetimeLocalValue = (hktTime) => {
    if (!hktTime) return '';
    const date = new Date(hktTime);
    if (isNaN(date.getTime())) return '';
    const isoString = date.toISOString();
    return isoString.slice(0, 16); // YYYY-MM-DDThh:mm
  };

  return (
    <div className="box" style={{ width: '95%', height: '500px' }}>
      <h3 style={{ textAlign: 'left', margin: '10px 0', fontSize: '20px', color: '#333' }}>
        Air Pollution Monitor
      </h3>

      {/* Time Range Selector, End Time Chooser, and Current Button */}
      <div style={{ marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '5px' }}>
          {['24h', '7days', 'all'].map(range => (
            <button
              key={range}
              style={{
                padding: '5px 15px',
                backgroundColor: timeRange === range ? '#e0e0e0' : 'transparent',
                border: '1px solid #ddd',
                borderRadius: '15px',
                cursor: 'pointer',
                fontWeight: timeRange === range ? 'bold' : 'normal',
              }}
              onClick={() => setTimeRange(range)}
            >
              {range === '24h' ? '24 Hours' : range === '7days' ? '7 Days' : 'All Data'}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
          <input
            type="datetime-local"
            value={getDatetimeLocalValue(endTime)}
            onChange={(e) => setEndTime(formatDatetimeLocalToHKT(e.target.value))}
            style={{
              padding: '5px',
              border: '1px solid #ddd',
              borderRadius: '5px',
              fontSize: '14px',
              cursor: 'pointer',
            }}
            title="Select end time (HKT)"
            disabled={useCurrentTime} // Disable picker when Current is on
          />
          <button
            onClick={() => setUseCurrentTime(!useCurrentTime)}
            style={{
              padding: '5px 10px',
              backgroundColor: useCurrentTime ? '#e0e0e0' : 'transparent',
              border: '1px solid #ddd',
              borderRadius: '15px',
              cursor: 'pointer',
              fontWeight: useCurrentTime ? 'bold' : 'normal',
            }}
            title={useCurrentTime ? 'Use custom end time' : 'Use current time'}
          >
            Current
          </button>
        </div>
      </div>

      {/* Data Type Toggle */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '10px',
          position: 'relative',
        }}
      >
        <div
          style={{
            position: 'absolute',
            width: '50px',
            height: '30px',
            backgroundColor: '#e0e0e0',
            borderRadius: '15px',
            left: selectedDataType === 'co2' ? 'calc(50% - 65px)' : 'calc(50% + 15px)',
            transition: 'left 0.3s ease',
            zIndex: 1,
          }}
        />
        <span
          style={{
            padding: '5px 20px',
            cursor: 'pointer',
            zIndex: 2,
            color: selectedDataType === 'co2' ? '#333' : '#666',
            fontWeight: selectedDataType === 'co2' ? 'bold' : 'normal',
          }}
          onClick={() => setSelectedDataType('co2')}
        >
          CO<sub>2</sub>
        </span>
        <span style={{ padding: '5px 5px', color: '#666' }}>|</span>
        <span
          style={{
            padding: '5px 20px',
            cursor: 'pointer',
            zIndex: 2,
            color: selectedDataType === 'voc' ? '#333' : '#666',
            fontWeight: selectedDataType === 'voc' ? 'bold' : 'normal',
          }}
          onClick={() => setSelectedDataType('voc')}
        >
          VOC
        </span>
      </div>

      {/* Status Messages */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '10px', color: '#666' }}>
          Loading data...
        </div>
      )}
      {error && (
        <div style={{ textAlign: 'center', padding: '10px', color: 'red' }}>
          {error}
          <button
            onClick={fetchData}
            style={{
              marginLeft: '10px',
              padding: '2px 10px',
              cursor: 'pointer',
            }}
          >
            Retry
          </button>
        </div>
      )}

      {/* Map */}
      <MapContainer
        center={[22.3193, 114.1694]}
        zoom={12}
        style={{ width: '100%', height: 'calc(100% - 130px)' }}
      >
        <TileLayer
          attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {dataPoints.map((point, index) => (
          <Circle
            key={index}
            center={[point.lat, point.lng]}
            radius={500}
            pathOptions={{
              fillColor: getCircleColor(
                selectedDataType === 'co2' ? point.co2 : point.voc,
                selectedDataType
              ),
              fillOpacity: 0.35,
              color: '#000000',
              weight: 1,
              opacity: 0.8,
            }}
          />
        ))}
      </MapContainer>
    </div>
  );
}